import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import NavButton from '@atoms/NavButton/NavButton';
import { ILink } from '@models/ILink';
import BreakpointTypeEnum from '@models/enums/BreakpointTypeEnum';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';

interface Props {
  readonly title?: string;
  readonly subtitle?: string;
  readonly cta?: ILink;
  readonly hideNavButtons?: boolean;
  readonly showYellowTick?: boolean;
  readonly subtitleClass?: string;
  readonly hasTextMaxWidth?: boolean;
  readonly dataTestId?: string;
  readonly navButtonId?: string;
  readonly isLeftNavButtonDisabled?: boolean;
  readonly isRightNavButtonDisabled?: boolean;
}

const ComponentHeader = ({
  title,
  subtitle,
  cta,
  hideNavButtons,
  showYellowTick = false,
  subtitleClass = 'body-one',
  hasTextMaxWidth = false,
  dataTestId,
  navButtonId,
  isLeftNavButtonDisabled,
  isRightNavButtonDisabled,
}: Props) => {
  const isMobileViewport = useMediaQuery(1024);
  const isMobileScreen = useMediaQuery(1024, BreakpointTypeEnum.Screen);

  return (
    <div
      className={classNames(
        'container gap-x-4 max-lg:flex lg:grid lg:grid-cols-12',
        navButtonId ? 'max-lg:justify-between' : 'max-lg:flex-col'
      )}
      data-testid={dataTestId}
    >
      {(title || subtitle) && (
        <div
          className={`mb-7.5 flex flex-col gap-2.5 ${
            cta?.url || navButtonId
              ? 'lg:col-span-9'
              : `lg:col-span-12 ${hasTextMaxWidth ? 'max-w-[812px]' : ''}`
          } lg:mb-0`}
        >
          <div className="flex items-center gap-5">
            {title && <h2>{title}</h2>}
            {showYellowTick && (
              <Icon
                className="shrink-0"
                name="check_circle"
                colorClassName="text-yellow"
                size={isMobileViewport ? '1.275rem' : '1.875rem'}
              />
            )}
          </div>
          {subtitle && <p className={subtitleClass}>{subtitle}</p>}
          {cta?.url && navButtonId && (
            <Button
              link={cta}
              linkCallback={() => {
                updateDataLayer({
                  event: 'selectContent',
                  content_type: cta.name,
                });
              }}
              className="mt-1.5 w-fit"
            />
          )}
        </div>
      )}

      {(cta?.url || navButtonId) && (
        <div
          className={classNames(
            'lg:col-span-3 lg:col-start-10 lg:flex lg:flex-col lg:items-end lg:gap-2.5',
            navButtonId
              ? 'flex items-end max-lg:mb-7.5 lg:justify-center'
              : 'lg:justify-end'
          )}
        >
          {navButtonId && (
            <div
              className={classNames(
                'gap-5',
                isMobileScreen || hideNavButtons ? 'hidden' : 'flex'
              )}
            >
              <NavButton
                id={navButtonId}
                direction={HorizontalDirectionEnum.Left}
                disabled={isLeftNavButtonDisabled}
              />
              <NavButton
                id={navButtonId}
                direction={HorizontalDirectionEnum.Right}
                disabled={isRightNavButtonDisabled}
              />
            </div>
          )}

          {cta?.url && !navButtonId && (
            <Button
              link={cta}
              linkCallback={() => {
                updateDataLayer({
                  event: 'selectContent',
                  content_type: cta.name,
                });
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ComponentHeader;
